import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Megaphone, AlertTriangle, Info, Users, Send, Trash2, Eye, CheckCircle2,
  Clock, Power, PowerOff, Loader2, Globe, Search, UserX, UserCheck, Copy, BellRing,
} from "lucide-react";

function frDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
}

const DEFAULT_SIGN = "La Direction\nSHARITI";

// Cible spéciale : tous les collaborateurs, toutes associations confondues.
const ALL_TARGET = "ALL";

// Aperçu brandé (identique au pop-up vu par le collaborateur).
function AnnouncementPreview({ title, body, type, signature, assocAcronym }) {
  const important = type === "important";
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] shadow-[var(--shadow-sm)]" data-testid="annonce-preview">
      <div className="flex items-center gap-2 bg-[var(--shariti-orange)] px-4 py-3 text-white">
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/20">
          {important ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-90">SHARITI · Annonce</div>
          <div className="truncate text-xs font-semibold">{assocAcronym || "Association"}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${important ? "bg-white text-[#9A6207]" : "bg-white/20 text-white"}`}>
          {important ? "⚠️ Important" : "ℹ️ Info"}
        </span>
      </div>
      <div className="bg-white px-4 py-4">
        <h3 className="text-base font-extrabold leading-snug text-ink">{title || "Titre de l'annonce"}</h3>
        <div className="mt-3 max-h-[40vh] overflow-y-auto whitespace-pre-wrap break-words text-[13px] leading-relaxed text-ink">
          {body || "Le contenu de votre message apparaîtra ici…"}
        </div>
        {signature ? (
          <div className="mt-4 whitespace-pre-wrap text-right text-[12px] font-semibold italic text-muted-ink">{signature}</div>
        ) : null}
      </div>
      <div className="border-t border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3 text-center text-[11px] font-medium text-muted-ink">
        Accusé de lecture obligatoire (case à cocher + bouton)
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "acknowledged")
    return <Badge className="border-0 bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" data-testid="status-ack"><CheckCircle2 className="mr-1 h-3 w-3" />Lu</Badge>;
  if (status === "seen")
    return <Badge className="border-0 bg-[rgba(214,138,20,0.16)] text-[#9A6207]" data-testid="status-seen"><Eye className="mr-1 h-3 w-3" />Vu</Badge>;
  return <Badge className="border-0 bg-[var(--surface-2)] text-muted-ink" data-testid="status-pending">Non vu</Badge>;
}

export default function Annonces() {
  const [associations, setAssociations] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ association_id: "", type: "important", title: "", body: "", signature: DEFAULT_SIGN });
  const [recipCount, setRecipCount] = useState(null);
  const [recipList, setRecipList] = useState([]);          // destinataires résolus pour la cible
  const [excluded, setExcluded] = useState(new Set());     // rd_id EXCLUS (ne recevront pas)
  const [recipOpen, setRecipOpen] = useState(false);       // dialog de gestion des destinataires
  const [recipQuery, setRecipQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);   // confirmation de relance
  const [resending, setResending] = useState(false);
  const [detailQuery, setDetailQuery] = useState("");       // recherche dans le suivi de lecture
  const pendingExcludeRef = useRef(null);                   // exclusions à ré-appliquer (duplication)

  const loadList = () => api.get("/announcements").then((r) => setList(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/associations"), api.get("/announcements")])
      .then(([a, l]) => { setAssociations(a.data || []); setList(l.data || []); })
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, []);

  // Aperçu des destinataires (liste + nombre) quand l'association change.
  // On RÉINITIALISE les exclusions à chaque changement de cible, puis on ré-applique
  // d'éventuelles exclusions « en attente » (cas de la duplication d'une annonce).
  useEffect(() => {
    if (!form.association_id) { setRecipCount(null); setRecipList([]); setExcluded(new Set()); return; }
    let alive = true;
    setRecipCount(null); setRecipList([]); setExcluded(new Set());
    api.get("/announcements/recipients/preview", { params: { association_id: form.association_id } })
      .then((r) => {
        if (!alive) return;
        const rl = r.data.recipients || [];
        setRecipCount(r.data.count);
        setRecipList(rl);
        if (pendingExcludeRef.current) {
          const valid = new Set(rl.map((x) => x.rd_id));
          setExcluded(new Set([...pendingExcludeRef.current].filter((id) => valid.has(id))));
          pendingExcludeRef.current = null;
        }
      })
      .catch(() => { if (alive) { setRecipCount(null); setRecipList([]); } });
    return () => { alive = false; };
  }, [form.association_id]);

  const assocById = useMemo(() => Object.fromEntries(associations.map((a) => [a.id, a])), [associations]);
  const selectedAssoc = assocById[form.association_id];
  const isAll = form.association_id === ALL_TARGET;
  const targetLabel = isAll ? "Toutes les associations" : selectedAssoc?.name;
  const previewAcronym = isAll ? "TOUTES" : (selectedAssoc?.acronym || selectedAssoc?.name);

  // Destinataires effectifs = total ciblé − exclus.
  const totalRecip = recipList.length;
  const effectiveCount = Math.max(0, totalRecip - excluded.size);
  const filteredRecipList = useMemo(() => {
    const q = recipQuery.trim().toLowerCase();
    if (!q) return recipList;
    return recipList.filter((r) => `${r.name || ""} ${r.code || ""}`.toLowerCase().includes(q));
  }, [recipList, recipQuery]);

  const toggleExcluded = (rdId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(rdId)) next.delete(rdId); else next.add(rdId);
      return next;
    });
  };

  const canSend = form.association_id && form.title.trim() && form.body.trim() && effectiveCount > 0;

  const doSend = async () => {
    setConfirmSend(false);
    setSending(true);
    try {
      await api.post("/announcements", {
        title: form.title.trim(), body: form.body.trim(), association_id: form.association_id,
        type: form.type, signature: form.signature.trim(),
        exclude_rd_ids: Array.from(excluded),
      });
      toast.success(`Annonce diffusée à ${effectiveCount} destinataire(s).`);
      setForm((f) => ({ ...f, title: "", body: "" }));
      setExcluded(new Set());
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la diffusion");
    } finally { setSending(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { active: !a.active });
      loadList();
      if (detail?.id === a.id) openDetail(a.id);
    } catch { toast.error("Action impossible"); }
  };

  const del = async () => {
    const a = delTarget; setDelTarget(null);
    if (!a) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success("Annonce supprimée."); loadList(); if (detail?.id === a.id) setDetail(null); }
    catch { toast.error("Suppression impossible"); }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailQuery("");
    try { const r = await api.get(`/announcements/${id}`); setDetail(r.data); }
    catch { toast.error("Détail indisponible"); }
    finally { setDetailLoading(false); }
  };

  // Rediffusion rapide : pré-remplit le composer avec le contenu d'une annonce existante
  // (titre, message, type, signature, cible) et reporte ses exclusions éventuelles.
  const duplicateAnnonce = (a) => {
    const exSet = new Set(a.excluded_rd_ids || []);
    if (a.association_id === form.association_id && recipList.length) {
      const valid = new Set(recipList.map((x) => x.rd_id));
      setExcluded(new Set([...exSet].filter((id) => valid.has(id))));
    } else {
      pendingExcludeRef.current = exSet;
    }
    setForm({
      association_id: a.association_id || "",
      type: a.type || "important",
      title: a.title || "",
      body: a.body || "",
      signature: a.signature || DEFAULT_SIGN,
    });
    setDetail(null);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    toast.success("Annonce copiée dans le formulaire — ajustez puis diffusez.");
  };

  // Relance des non-lus : crée une nouvelle annonce ciblant uniquement ceux qui n'ont
  // pas encore accusé lecture (le pop-up réapparaît chez eux seulement).
  const doResend = async () => {
    const a = resendTarget; setResendTarget(null);
    if (!a) return;
    setResending(true);
    try {
      const r = await api.post(`/announcements/${a.id}/resend-unread`);
      const n = (r.data?.recipient_rd_ids || []).length;
      toast.success(`Relance envoyée à ${n} collaborateur(s) non lu(s).`);
      if (detail?.id === a.id) setDetail(null);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relance impossible");
    } finally { setResending(false); }
  };

  return (
    <div className="space-y-6" data-testid="annonces-page">
      <PageHeader
        title="Annonces"
        subtitle="Diffusez un message ciblé par association — il s'affiche en pop-up dans l'espace des collaborateurs, avec accusé de lecture et suivi."
      />

      {/* Composer + aperçu */}
      <div className="grid gap-4 lg:grid-cols-[1fr,380px]">
        <Card className="space-y-4 p-4 sm:p-5" data-testid="annonce-composer">
          <div className="flex items-center gap-2 text-sm font-bold text-ink">
            <Megaphone className="h-4 w-4 text-[var(--shariti-orange)]" /> Nouvelle annonce
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Association cible</Label>
              <Select value={form.association_id} onValueChange={(v) => setForm((f) => ({ ...f, association_id: v }))}>
                <SelectTrigger data-testid="annonce-assoc-select"><SelectValue placeholder="Choisir une cible" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TARGET} data-testid="annonce-assoc-ALL">
                    <span className="inline-flex items-center gap-1.5 font-bold text-[var(--shariti-orange)]">
                      <Globe className="h-3.5 w-3.5" /> Toutes les associations — tous les collaborateurs
                    </span>
                  </SelectItem>
                  {associations.length > 0 && <SelectSeparator />}
                  {associations.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`annonce-assoc-${a.acronym || a.id}`}>
                      {a.name}{a.acronym ? ` (${a.acronym})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.association_id && (
                <div className="space-y-1.5 pt-0.5" data-testid="annonce-recip-count">
                  <div className="flex items-center gap-1.5 text-[12px] font-semibold text-muted-ink">
                    <Users className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                    {recipCount == null ? "Calcul…" : (
                      <span>
                        <strong className="text-ink">{effectiveCount}</strong> destinataire(s){isAll ? " (toutes associations)" : ""}
                        {excluded.size > 0 && <span className="text-[#9B2226]"> · {excluded.size} exclu(s)</span>}
                      </span>
                    )}
                  </div>
                  {recipCount != null && totalRecip > 0 && (
                    <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-[12px]"
                      onClick={() => { setRecipQuery(""); setRecipOpen(true); }} data-testid="annonce-manage-recipients">
                      <UserX className="h-3.5 w-3.5" />
                      {excluded.size > 0 ? "Gérer les exclusions" : "Exclure des collaborateurs"}
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="annonce-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="important" data-testid="annonce-type-important">⚠️ Important</SelectItem>
                  <SelectItem value="info" data-testid="annonce-type-info">ℹ️ Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Titre</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex. ⚠️ IMPORTANT – Diversification des profils de donateurs" data-testid="annonce-title-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={10} placeholder="Rédigez votre message… (les retours à la ligne sont conservés)" data-testid="annonce-body-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Signature</Label>
            <Textarea value={form.signature} onChange={(e) => setForm((f) => ({ ...f, signature: e.target.value }))}
              rows={2} data-testid="annonce-signature-input" />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button disabled={!canSend || sending} onClick={() => setConfirmSend(true)}
              className="bg-[var(--shariti-orange)] font-bold text-white hover:bg-[#E56622]" data-testid="annonce-send-btn">
              {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
              Diffuser l'annonce
            </Button>
          </div>
        </Card>

        <div className="space-y-2">
          <div className="px-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Aperçu (vu par le collaborateur)</div>
          <AnnouncementPreview title={form.title} body={form.body} type={form.type}
            signature={form.signature} assocAcronym={previewAcronym} />
        </div>
      </div>

      {/* Liste des annonces */}
      <Card className="p-4 sm:p-5" data-testid="annonce-list">
        <div className="mb-3 text-sm font-bold text-ink">Annonces diffusées</div>
        {loading ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" /></div>
        ) : list.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-ink">Aucune annonce pour le moment.</div>
        ) : (
          <div className="space-y-2">
            {list.map((a) => {
              const s = a.stats || {};
              const unread = Math.max(0, (s.total || 0) - (s.acknowledged || 0));
              return (
                <div key={a.id} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`annonce-row-${a.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${a.type === "important" ? "bg-[rgba(214,138,20,0.16)] text-[#9A6207]" : "bg-[var(--surface-2)] text-muted-ink"}`}>
                          {a.type === "important" ? "⚠️ Important" : "ℹ️ Info"}
                        </span>
                        {a.association_acronym ? <Badge variant="outline" className="text-[10px]">{a.association_acronym}</Badge> : null}
                        {a.is_resend ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]">
                            <BellRing className="h-3 w-3" />Relance
                          </span>
                        ) : null}
                        {a.active ? (
                          <span className="text-[10px] font-bold text-[#0F5A2F]">● Active</span>
                        ) : (
                          <span className="text-[10px] font-bold text-muted-ink">● Archivée</span>
                        )}
                      </div>
                      <div className="mt-1 truncate text-sm font-bold text-ink">{a.title}</div>
                      <div className="text-[11px] text-muted-ink">Diffusée le {frDateTime(a.created_at)} · par {a.created_by_name || "—"}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(a.id)} data-testid={`annonce-detail-${a.id}`}>
                        <Eye className="mr-1 h-4 w-4" /> Détails
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateAnnonce(a)}
                        title="Dupliquer (pré-remplir le formulaire)" data-testid={`annonce-duplicate-${a.id}`}>
                        <Copy className="h-4 w-4 text-muted-ink" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 disabled:opacity-40" onClick={() => setResendTarget(a)}
                        disabled={unread === 0} title={unread === 0 ? "Tout le monde a lu" : `Relancer ${unread} non-lu(s)`}
                        data-testid={`annonce-resend-${a.id}`}>
                        <BellRing className={`h-4 w-4 ${unread > 0 ? "text-[var(--shariti-orange)]" : "text-muted-ink"}`} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleActive(a)}
                        title={a.active ? "Archiver (ne plus afficher)" : "Réactiver"} data-testid={`annonce-toggle-${a.id}`}>
                        {a.active ? <PowerOff className="h-4 w-4 text-muted-ink" /> : <Power className="h-4 w-4 text-[#0F5A2F]" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9B2226] hover:bg-[rgba(214,69,69,0.12)]"
                        onClick={() => setDelTarget(a)} title="Supprimer" data-testid={`annonce-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-3">
                    <Progress value={s.rate || 0} className="h-2 flex-1" data-testid={`annonce-progress-${a.id}`} />
                    <span className="shrink-0 text-[12px] font-bold text-ink" data-testid={`annonce-rate-${a.id}`}>
                      {s.acknowledged || 0}/{s.total || 0} lu ({s.rate || 0}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Détail : suivi de lecture + message diffusé (en onglets) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden" data-testid="annonce-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{detail?.title}</DialogTitle>
          </DialogHeader>
          {detail && (() => {
            const stats = detail.stats || {};
            const total = stats.total || 0;
            const ack = stats.acknowledged || 0;
            const seenTotal = stats.seen || 0;             // inclut ceux qui ont aussi LU
            const seen = Math.max(0, seenTotal - ack);     // VU mais pas encore lu
            const notSeen = Math.max(0, total - seenTotal); // jamais ouvert
            const unread = Math.max(0, total - ack);
            const q = detailQuery.trim().toLowerCase();
            const recips = (detail.recipients || []).filter((r) =>
              !q || `${r.name || ""} ${r.code || ""}`.toLowerCase().includes(q));
            return (
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                {/* Horodatage de diffusion (toujours visible) */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-ink" data-testid="detail-meta">
                  <span className="inline-flex items-center gap-1 font-semibold text-ink">
                    <Clock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                    Diffusée le {frDateTime(detail.created_at)}
                  </span>
                  <span>par {detail.created_by_name || "—"}</span>
                  <span className="font-semibold text-ink">{detail.association_name}{detail.association_acronym ? ` (${detail.association_acronym})` : ""}</span>
                  <span className={`rounded-full px-2 py-0.5 font-bold ${detail.type === "important" ? "bg-[rgba(214,138,20,0.16)] text-[#9A6207]" : "bg-[var(--surface-2)] text-muted-ink"}`}>
                    {detail.type === "important" ? "⚠️ Important" : "ℹ️ Info"}
                  </span>
                  {detail.active
                    ? <span className="font-bold text-[#0F5A2F]">● Active</span>
                    : <span className="font-bold text-muted-ink">● Archivée</span>}
                </div>

                <Tabs defaultValue="reads" className="flex min-h-0 flex-1 flex-col">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="reads" data-testid="detail-tab-reads">Suivi de lecture ({ack}/{total})</TabsTrigger>
                    <TabsTrigger value="message" data-testid="detail-tab-message">Message diffusé</TabsTrigger>
                  </TabsList>

                  {/* ONGLET 1 — Suivi de lecture (par défaut) */}
                  <TabsContent value="reads" className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden data-[state=inactive]:hidden">
                    {/* Compteurs */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="rounded-xl bg-[rgba(31,157,85,0.10)] p-2 text-center" data-testid="detail-count-ack">
                        <div className="text-base font-extrabold text-[#0F5A2F]">{ack}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-[#0F5A2F]">Lu</div>
                      </div>
                      <div className="rounded-xl bg-[rgba(214,138,20,0.12)] p-2 text-center" data-testid="detail-count-seen">
                        <div className="text-base font-extrabold text-[#9A6207]">{seen}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-[#9A6207]">Vu, non lu</div>
                      </div>
                      <div className="rounded-xl bg-[var(--surface-2)] p-2 text-center" data-testid="detail-count-pending">
                        <div className="text-base font-extrabold text-muted-ink">{notSeen}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Non vu</div>
                      </div>
                      <div className="rounded-xl bg-[rgba(238,114,46,0.12)] p-2 text-center" data-testid="detail-count-rate">
                        <div className="text-base font-extrabold text-[var(--shariti-orange)]">{stats.rate || 0}%</div>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">Taux de lecture</div>
                      </div>
                    </div>

                    {(detail.excluded_recipients || []).length > 0 && (
                      <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[rgba(155,34,38,0.04)] p-2.5" data-testid="detail-excluded">
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#9B2226]">
                          <UserX className="h-3.5 w-3.5" />{detail.excluded_recipients.length} exclu(s) à la diffusion
                        </div>
                        <div className="mt-0.5 text-[12px] text-muted-ink">
                          {detail.excluded_recipients.map((r) => r.name).filter(Boolean).join(", ")}
                        </div>
                      </div>
                    )}

                    {/* Recherche */}
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
                      <Input value={detailQuery} onChange={(e) => setDetailQuery(e.target.value)}
                        placeholder="Rechercher un collaborateur…" className="h-9 pl-8 text-[13px]" data-testid="detail-recip-search" />
                    </div>

                    {/* Tableau détaillé daté (Vu le + Lu le) */}
                    <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-[var(--border-soft)]">
                      <table className="w-full text-left text-[12px]">
                        <thead className="sticky top-0 z-10 bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                          <tr>
                            <th className="px-3 py-2 font-bold">Collaborateur</th>
                            <th className="px-3 py-2 font-bold">Statut</th>
                            <th className="px-3 py-2 font-bold">Vu le</th>
                            <th className="px-3 py-2 font-bold">Lu le</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recips.length === 0 ? (
                            <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-ink">Aucun collaborateur trouvé.</td></tr>
                          ) : recips.map((r) => (
                            <tr key={r.rd_id} className="border-t border-[var(--border-soft)] hover:bg-[var(--surface-2)]" data-testid={`detail-recip-${r.code || r.rd_id}`}>
                              <td className="px-3 py-2 font-semibold text-ink">
                                {r.name}{r.code ? <span className="ml-1 font-normal text-muted-ink">· {r.code}</span> : null}
                              </td>
                              <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                              <td className="px-3 py-2 text-muted-ink" data-testid={`detail-seen-at-${r.code || r.rd_id}`}>
                                {r.seen_at ? (
                                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{frDateTime(r.seen_at)}</span>
                                ) : "—"}
                              </td>
                              <td className="px-3 py-2 text-muted-ink" data-testid={`detail-ack-at-${r.code || r.rd_id}`}>
                                {r.acknowledged_at ? (
                                  <span className="inline-flex items-center gap-1 font-semibold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" />{frDateTime(r.acknowledged_at)}</span>
                                ) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  {/* ONGLET 2 — Message diffusé (aperçu exact) */}
                  <TabsContent value="message" className="mt-3 min-h-0 flex-1 overflow-y-auto data-[state=inactive]:hidden">
                    <AnnouncementPreview title={detail.title} body={detail.body} type={detail.type}
                      signature={detail.signature} assocAcronym={detail.association_acronym || detail.association_name} />
                  </TabsContent>
                </Tabs>

                {/* Actions : rediffusion rapide + relance des non-lus (toujours visibles) */}
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border-soft)] pt-3">
                  <Button variant="outline" size="sm" onClick={() => duplicateAnnonce(detail)} data-testid="detail-duplicate">
                    <Copy className="mr-1.5 h-4 w-4" />Dupliquer
                  </Button>
                  <Button size="sm" disabled={unread === 0 || resending}
                    onClick={() => setResendTarget(detail)}
                    className="bg-[var(--shariti-orange)] font-bold text-white hover:bg-[#E56622] disabled:opacity-40"
                    data-testid="detail-resend">
                    <BellRing className="mr-1.5 h-4 w-4" />
                    {unread === 0 ? "Tout le monde a lu" : `Relancer les non-lus (${unread})`}
                  </Button>
                </div>
              </div>
            );
          })()}
          {detailLoading && <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[var(--shariti-orange)]" /></div>}
        </DialogContent>
      </Dialog>

      {/* Gestion des destinataires (exclusions) */}
      <Dialog open={recipOpen} onOpenChange={setRecipOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden" data-testid="annonce-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Destinataires de l'annonce</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[12px] text-muted-ink">
              Décochez un collaborateur pour qu'il <strong>ne reçoive pas</strong> cette annonce. Par défaut, tout le monde est inclus.
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
                <Input value={recipQuery} onChange={(e) => setRecipQuery(e.target.value)}
                  placeholder="Rechercher un nom ou un code…" className="h-9 pl-8 text-[13px]" data-testid="annonce-recip-search" />
              </div>
              <Button type="button" variant="outline" size="sm" className="h-9 shrink-0 text-[12px]"
                onClick={() => setExcluded(new Set())} disabled={excluded.size === 0} data-testid="annonce-recip-include-all">
                <UserCheck className="mr-1 h-3.5 w-3.5" />Tout inclure
              </Button>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[var(--surface-2)] px-3 py-2 text-[12px] font-bold">
              <span className="inline-flex items-center gap-1.5 text-[#0F5A2F]"><UserCheck className="h-3.5 w-3.5" />{effectiveCount} recevront</span>
              <span className="inline-flex items-center gap-1.5 text-[#9B2226]"><UserX className="h-3.5 w-3.5" />{excluded.size} exclu(s)</span>
            </div>
            <ScrollArea className="h-[45vh] rounded-xl border border-[var(--border-soft)]">
              <div className="divide-y divide-[var(--border-soft)]">
                {filteredRecipList.length === 0 ? (
                  <div className="py-8 text-center text-[13px] text-muted-ink">Aucun collaborateur trouvé.</div>
                ) : filteredRecipList.map((r) => {
                  const isIncluded = !excluded.has(r.rd_id);
                  return (
                    <label key={r.rd_id} htmlFor={`recip-${r.rd_id}`}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-[var(--surface-2)]"
                      data-testid={`annonce-recip-item-${r.code || r.rd_id}`}>
                      <Checkbox id={`recip-${r.rd_id}`} checked={isIncluded}
                        onCheckedChange={() => toggleExcluded(r.rd_id)}
                        data-testid={`annonce-recip-checkbox-${r.code || r.rd_id}`} />
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-[13px] font-semibold ${isIncluded ? "text-ink" : "text-muted-ink line-through"}`}>{r.name}</div>
                        {r.code ? <div className="text-[11px] text-muted-ink">{r.code}</div> : null}
                      </div>
                      {!isIncluded && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#9B2226]">Exclu</span>}
                    </label>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="flex justify-end">
              <Button type="button" onClick={() => setRecipOpen(false)}
                className="bg-[var(--shariti-orange)] font-bold text-white hover:bg-[#E56622]" data-testid="annonce-recip-done">
                Terminé
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation de diffusion */}
      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent data-testid="annonce-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Diffuser cette annonce ?</AlertDialogTitle>
            <AlertDialogDescription>
              Elle apparaîtra en pop-up bloquant à <strong>{effectiveCount}</strong> collaborateur(s) et responsable(s) de{" "}
              <strong>{targetLabel}</strong> disposant d'un espace{excluded.size > 0 ? ` (${excluded.size} exclu(s))` : ""}. Aucun e-mail n'est envoyé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="annonce-confirm-send">
              Diffuser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation de suppression */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="annonce-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette annonce ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'annonce « {delTarget?.title} » et ses statistiques de lecture seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#9B2226] text-white hover:bg-[#7f1d1f]" data-testid="annonce-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation de relance des non-lus */}
      <AlertDialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <AlertDialogContent data-testid="annonce-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Relancer les non-lus ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le pop-up « {resendTarget?.title} » sera renvoyé uniquement aux{" "}
              <strong>{Math.max(0, (resendTarget?.stats?.total || 0) - (resendTarget?.stats?.acknowledged || 0))}</strong>{" "}
              collaborateur(s) qui ne l'ont pas encore lu. Une nouvelle entrée de relance apparaîtra dans la liste. Aucun e-mail n'est envoyé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doResend} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="annonce-resend-confirm">
              Relancer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
